import React from "react";
import "./style.scss";

const PrivacyPolicy = () => {
  return (
    <div
      style={{ maxWidth: "1100px", margin: "auto" }}
      className="text-start privacy p-5"
    >
      <h1 className="mb-4 fw-bold">Privacy Policy</h1>
      <p className="mb-1">
        <strong>Effective date:</strong> November 22, 2025
      </p>

      <p>
        Saadi Gaadi (“we”, “us”, or “our”) operates the Saadi Gaadi mobile
        application and related services (the “Service”). This Privacy Policy
        explains what information we collect, how we use it, with whom we share
        it, how long we keep it, and your choices about your information.
      </p>

      <h2>Scope</h2>
      <p>
        This Privacy Policy applies to information collected by the Saadi Gaadi
        mobile application and any associated websites or services. By using the
        Service you consent to the collection, use and sharing of your
        information as described in this policy.
      </p>

      <h2>Information We Collect</h2>
      <p>
        We collect information needed to provide ride/driver services, ensure
        safety, and improve the Service. The categories below describe the types
        of information we collect.
      </p>

      <h3>Account & Contact Information</h3>
      <ul>
        <li>Full name</li>
        <li>Phone number and SMS verification (if used)</li>
        <li>Email address (if provided)</li>
      </ul>

      <h3>Device & Usage Information</h3>
      <p>
        We collect device identifiers and diagnostic/usage data when you use the
        app. This includes device model, OS version, app version, IP address,
        crash logs, and analytics data to understand how the Service is used.
      </p>

      <h3>Location Information (Precise & Background)</h3>
      <p>
        <strong>
          We collect precise device location information (GPS, network, and
          sensor-based) when you use the Service.
        </strong>{" "}
        In addition, if you grant permission and you enable features that
        require it, we may collect
        <strong> background location</strong> (location while the app is in the
        background) to support core functionality such as driver availability,
        ride matching, navigation, and safety features.
      </p>

      <h4>Why we collect location</h4>
      <ul>
        <li>To provide real-time driver location during an active trip.</li>
        <li>To match drivers with nearby ride requests and estimate ETAs.</li>
        <li>For turn-by-turn navigation and routing during a trip.</li>
        <li>
          To record the route taken for billing, trip history, and dispute
          resolution.
        </li>
        <li>
          To provide safety features (for example: emergency assistance,
          incident investigations).
        </li>
      </ul>

      <h4>How location is collected and transmitted</h4>
      <p>
        When permitted by you, the app reads location from your device and
        transmits it securely to our servers over encrypted transport (HTTPS).
        Location may be updated periodically (for example, every few seconds
        during an active trip) or at intervals required for background features.
      </p>

      <h4>Do we require background location?</h4>
      <p>
        Background location is only used when the driver enables features that
        require it (for example, to receive ride requests while the app is not
        in the foreground). You can decline background location access; if you
        do, some features (such as receiving ride requests while app is closed
        or accurate availability tracking) may not work correctly.
      </p>

      <h2>Use of Data</h2>
      <p>We use the collected data for these purposes:</p>
      <ul>
        <li>
          To operate and provide the Service (match riders & drivers, routing).
        </li>
        <li>To process payments and trip billing (if applicable).</li>
        <li>To investigate safety incidents, fraud, or disputes.</li>
        <li>To improve and optimize the Service and perform analytics.</li>
        <li>
          To send operational messages (receipts, support communications).
        </li>
      </ul>

      <h2>Data Sharing & Third Parties</h2>
      <p>We may share collected information with:</p>
      <ul>
        <li>
          Riders and drivers involved in a trip (live location during active
          trip).
        </li>
        <li>
          Service providers who perform services on our behalf (e.g., hosting,
          analytics, maps, crash reporting, payment processors).
        </li>
        <li>
          Law enforcement or regulators when required by law or to respond to
          legal processes.
        </li>
        <li>Other parties with your consent.</li>
      </ul>
      <p>
        <strong>Third-party services we use may include:</strong> Google Maps
        Platform (Maps/Places/Routes), Firebase (analytics/crash reporting),
        Crashlytics, payment gateways, and other providers. These third parties
        may collect and process personal data in accordance with their own
        privacy policies.
      </p>

      <h2>Data Retention</h2>
      <p>
        We retain personal data only for as long as necessary to provide the
        Service and to comply with legal obligations. Typical retention periods:
      </p>
      <ul>
        <li>
          Trip & location data: retained for up to <strong>180 days</strong>{" "}
          after the trip ends for billing, analytics and dispute resolution.
        </li>
        <li>
          Account information: retained while the account is active and for a
          reasonable period after account deletion to comply with legal
          obligations (for example, tax or regulatory requirements).
        </li>
        <li>
          Aggregated/anonymous analytics: retained for up to{" "}
          <strong>24 months</strong>.
        </li>
      </ul>
      <p>
        If you need a different retention period for your data, contact us using
        the details below; we will consider requests and act as required by law.
      </p>

      <h2>Your Choices & Controls</h2>
      <h4>Location permissions</h4>
      <p>
        You control location access through your device settings. To stop
        location collection, revoke the app’s location permission in your
        device’s OS settings. Revoking permission may limit the app’s
        functionality (for example, you may not receive ride requests or
        accurate navigation).
      </p>

      <h4>Opt-out of analytics</h4>
      <p>
        You may opt-out of certain analytics or advertising features by
        disabling advertising identifiers and related settings on your device.
        See your device settings for more details.
      </p>

      <h4>Delete or request access to your data</h4>
      <p>
        You can request access to, correction of, or deletion of your personal
        information by contacting us (see Contact Us below). We will respond to
        requests in accordance with applicable law.
      </p>

      <h2>Security</h2>
      <p>
        We take reasonable administrative, technical, and physical measures to
        protect personal data. No security measure is perfect, so we cannot
        guarantee absolute security of your information. If we learn of a data
        breach that creates a real risk of harm, we will notify affected users
        and regulators as required by law.
      </p>

      <h2>Children’s Privacy</h2>
      <p>
        Our Service is not intended for children under 18. We do not knowingly
        collect personal data from children under 18. If you believe we have
        collected such data, please contact us and we will remove it.
      </p>

      <h2>Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy to reflect changes in our practices or
        for legal, operational, or regulatory reasons. We will post the revised
        policy on this page with an updated effective date and, where
        appropriate, notify users by email or in-app notice prior to changes
        taking effect.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have questions, requests, or concerns about this Privacy Policy
        or our data practices, contact us:
      </p>
      <ul>
        <li>
          Email:{" "}
          <a href="mailto:saadigaddi.dev@gmail.com">
            saadigaddi.dev@gmail.com
          </a>
        </li>
      </ul>
    </div>
  );
};

export default PrivacyPolicy;
